import { useState, useMemo, useEffect } from 'react';
import { groupCategoriesByLegacyDivision } from '../../../shared/utils/masterformatMapping';

/**
 * View mode constants for the estimate-detail vocabulary toggle.
 *
 * Legacy aliases (`MODERN`/`LEGACY`) are kept so existing callers that
 * imported them still compile. New code should use the explicit ids.
 */
export const VOCABULARY_VIEW = {
  CUSTOM: 'custom',
  CSI50: 'csi50',
  CSI15: 'csi15',
};

// Backward-compat alias map: legacy mode id -> current id.
const NORMALIZE_MODE = {
  modern: VOCABULARY_VIEW.CSI50,
  legacy: VOCABULARY_VIEW.CSI15,
  csi50: VOCABULARY_VIEW.CSI50,
  csi15: VOCABULARY_VIEW.CSI15,
  custom: VOCABULARY_VIEW.CUSTOM,
};

// Old export names some files still reference. The values are now the new ids
// so any switch on these constants keeps working.
export const MASTERFORMAT_VIEW = {
  MODERN: VOCABULARY_VIEW.CSI50,
  LEGACY: VOCABULARY_VIEW.CSI15,
};

function normalizeMode(mode) {
  return NORMALIZE_MODE[mode] || VOCABULARY_VIEW.CSI50;
}

/**
 * Apply the Custom view shape to category data.
 *
 * Custom mode keeps the modern CSI 50 grouping but expands the row layout to
 * reflect the tenant's own vocabulary at source granularity:
 *
 *   - Line items with N>1 source rows are REPLACED by N virtual rows, each
 *     showing one source's canonical vocab name (or raw description if vocab
 *     match is missing) and its own per-source totalCost. The merged parent
 *     row is dropped from Custom view -- its data still lives on the API
 *     response, so CSI 50 view is unaffected.
 *   - Line items with 0 or 1 source row keep the existing single-row rewrite
 *     (canonical -> source description -> unchanged).
 *   - AI-only items (no source rows) pass through unchanged in both views.
 *
 * Virtual rows are marked `_isVirtualSourceRow: true` so the renderers can
 * disable expansion chevrons and the editor can render them read-only.
 */
function getCustomDisplayDescription(item) {
  const canonical = item.vocab_canonical_name || item.vocabCanonicalName;
  if (canonical) return canonical;

  const sources = item.sourceLineItems || item.source_line_items;
  if (Array.isArray(sources) && sources.length > 0) {
    const names = sources
      .map((s) => s?.description)
      .filter((d) => typeof d === 'string' && d.trim().length > 0);
    if (names.length === 1) return names[0];
    if (names.length > 1) return names.join(', ');
  }

  return null;
}

/**
 * Build a virtual line-item row from one source entry. Inherits classification
 * fields from the parent so MasterFormat-related UI and category subtotals
 * still resolve. Per-source quantity/unit/unitCost are not captured today, so
 * they're left null and render as em-dash.
 */
function buildVirtualSourceRow(parent, source, idx) {
  const canonical = source?.vocabCanonicalName || source?.vocab_canonical_name;
  const description =
    canonical || source?.description || parent.description || '';
  const parentId = parent.lineItemId || parent.line_item_id;
  return {
    lineItemId: `${parentId}::source::${idx}`,
    description,
    quantity: null,
    unit: null,
    unitCost: null,
    totalCost: source?.totalCost ?? source?.total_cost ?? null,
    notes: null,
    costSource: parent.costSource || parent.cost_source,
    masterformatCode: parent.masterformatCode || parent.masterformat_code,
    vocabEntryId: source?.vocabEntryId || source?.vocab_entry_id,
    vocabCanonicalName: canonical,
    sourceLineItems: null,
    _isVirtualSourceRow: true,
    _parentLineItemId: parentId,
  };
}

/**
 * Whether a line-item row's currently-displayed description came from its
 * source_line_items (rather than the AI-merged description). True when the
 * description text matches the same join the Custom-view rewrite would
 * produce.
 *
 * Used in two places:
 *  - LineItemExplanationPanel: hide the redundant "Derived from..." panel
 *  - LineItemRow: hide the expand chevron and disable the click-to-expand,
 *    since the only content the panel would show is the source list, which
 *    is now redundant.
 *
 * Not gated on viewMode -- if the rendered description happens to match the
 * sources in any mode, the redundancy is real either way.
 */
export function lineItemDescriptionMatchesSources(item) {
  const sources = item.sourceLineItems || item.source_line_items;
  if (!Array.isArray(sources) || sources.length === 0) return false;
  const sourceNames = sources
    .map((s) => s?.description)
    .filter((d) => typeof d === 'string' && d.trim().length > 0);
  if (sourceNames.length === 0) return false;
  const expected = sourceNames.length === 1 ? sourceNames[0] : sourceNames.join(', ');
  return (item.description || '').trim() === expected.trim();
}

function applyCustomView(categories) {
  if (!categories) return [];
  return categories.map((category) => {
    const lineItems = category.lineItems || category.line_items || [];
    if (!lineItems.length) return category;
    const transformed = [];
    for (const item of lineItems) {
      const sources = item.sourceLineItems || item.source_line_items;
      if (Array.isArray(sources) && sources.length > 1) {
        sources.forEach((source, idx) => {
          transformed.push(buildVirtualSourceRow(item, source, idx));
        });
        continue;
      }
      const display = getCustomDisplayDescription(item);
      if (!display || display === item.description) {
        transformed.push(item);
      } else {
        transformed.push({ ...item, description: display });
      }
    }
    return {
      ...category,
      lineItems: category.lineItems ? transformed : category.lineItems,
      line_items: category.line_items ? transformed : category.line_items,
    };
  });
}

/**
 * Custom hook for managing vocabulary view mode (Custom / CSI 50 / CSI 15).
 *
 * @param {Array} categories - Estimate categories (modern/CSI 50 shape from API)
 * @param {string} initialMode - Optional initial mode. Accepts the new ids
 *   ('custom' | 'csi50' | 'csi15') or the legacy ids ('modern' | 'legacy').
 *   Defaults to CSI 50.
 * @returns {Object} { viewMode, setViewMode, displayCategories,
 *   isCustomView, isLegacyView, isModernView, toggleView }
 */
export function useMasterFormatView(categories, initialMode = VOCABULARY_VIEW.CSI50) {
  const [viewMode, setViewMode] = useState(normalizeMode(initialMode));

  // If the parent's initialMode resolves later (e.g. tenant default arrives
  // after first render), pick it up exactly once.
  useEffect(() => {
    const next = normalizeMode(initialMode);
    setViewMode((prev) => (prev === next ? prev : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode]);

  const displayCategories = useMemo(() => {
    if (!categories || !Array.isArray(categories)) return [];
    if (viewMode === VOCABULARY_VIEW.CSI15) {
      return groupCategoriesByLegacyDivision(categories);
    }
    if (viewMode === VOCABULARY_VIEW.CUSTOM) {
      return applyCustomView(categories);
    }
    return categories;
  }, [categories, viewMode]);

  const setView = (mode) => setViewMode(normalizeMode(mode));

  // Cycle through the three modes, in the order the toggle displays them.
  const toggleView = () => {
    setViewMode((prev) => {
      if (prev === VOCABULARY_VIEW.CUSTOM) return VOCABULARY_VIEW.CSI50;
      if (prev === VOCABULARY_VIEW.CSI50) return VOCABULARY_VIEW.CSI15;
      return VOCABULARY_VIEW.CUSTOM;
    });
  };

  return {
    viewMode,
    setViewMode: setView,
    toggleView,
    displayCategories,
    isCustomView: viewMode === VOCABULARY_VIEW.CUSTOM,
    isLegacyView: viewMode === VOCABULARY_VIEW.CSI15,
    isModernView: viewMode === VOCABULARY_VIEW.CSI50,
  };
}
